import { Box } from '@mui/material';
import React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ImageSlicePositions } from '~/containers/viewer/viewport/Viewport';
import {
    formatDoseDisplayValue,
    formatValueWithUnit,
} from '~/domain/dicom/unitSuffix';
import { Orientations } from '~/engine/view/views/PlaneView';
import { doseVisibilityState } from '~/state/atoms/dose/doseAtoms';
import {
    secondaryImageValueAtCursorState,
    coordinateDisplayExpandedState,
    currentImageSliceOrientationPositionsState,
    doseIntensityValueAtCursorState,
    imageValueAtCursorState,
    patientSpaceCoordsState,
    sideBySideImageSliceOrientationPositionsState,
} from '~/state/atoms/viewerAtoms';
import { OverlayLabel, OverlayRow } from './OverlayLabel';
import {
    environmentState,
    secondaryEnvironmentState,
} from '~/state/atoms/engineAtoms';
interface CoordinateDisplayProps {
    viewportOrientation: Orientations;
    isActive: boolean;
    isPrimaryImageViewport: boolean;
}

const CoordinateDisplay = ({
    viewportOrientation,
    isActive,
    isPrimaryImageViewport,
}: CoordinateDisplayProps) => {
    const environment = useRecoilValue(environmentState);
    const secondaryEnvironment = useRecoilValue(secondaryEnvironmentState);

    const primaryImage = isPrimaryImageViewport
        ? environment?.image?.parent
        : secondaryEnvironment?.image?.parent;
    const secondaryImage = isPrimaryImageViewport
        ? environment?.secondaryImage?.parent
        : secondaryEnvironment?.secondaryImage?.parent;

    const patientSpaceCoords = useRecoilValue(patientSpaceCoordsState);
    const currentImageSliceOrientationPositions =
        useRecoilValue<ImageSlicePositions>(
            currentImageSliceOrientationPositionsState
        );
    const sideBySideImageSliceOrientationPositions =
        useRecoilValue<ImageSlicePositions>(
            sideBySideImageSliceOrientationPositionsState
        );
    const imageValueAtCursor = useRecoilValue(imageValueAtCursorState);
    const secondaryImageValueAtCursor = useRecoilValue(
        secondaryImageValueAtCursorState
    );
    const doseIntensityValueAtCursor = useRecoilValue(
        doseIntensityValueAtCursorState
    );
    const doseVisibility = useRecoilValue(doseVisibilityState);
    const [coordinateDisplayExpanded, setCoordinateDisplayExpanded] =
        useRecoilState(coordinateDisplayExpandedState);
    const expanded = !!(
        coordinateDisplayExpanded?.includes(viewportOrientation) &&
        primaryImage?.firstSlice &&
        patientSpaceCoords?.coords
    );

    const handleExpand = () => {
        if (expanded) {
            const newExpanded = coordinateDisplayExpanded?.filter(
                (o) => o !== viewportOrientation
            );
            setCoordinateDisplayExpanded(
                newExpanded?.length ? newExpanded : null
            );
        } else {
            setCoordinateDisplayExpanded([
                ...(coordinateDisplayExpanded || []),
                viewportOrientation,
            ]);
        }
    };

    // Don't render any coordinate info if there's no image loaded in this viewport
    if (!primaryImage) {
        return null;
    }

    const axis =
        viewportOrientation === Orientations.SAGITTAL_ORIENTATION
            ? 'X'
            : viewportOrientation === Orientations.TRANSVERSAL_ORIENTATION
              ? 'Z'
              : 'Y';

    const primaryPosition = isPrimaryImageViewport
        ? currentImageSliceOrientationPositions[
              viewportOrientation as keyof ImageSlicePositions
          ]
        : sideBySideImageSliceOrientationPositions[
              viewportOrientation as keyof ImageSlicePositions
          ];

    const formattedImageValue =
        imageValueAtCursor !== null && primaryImage?.seriesMetadata.modality
            ? formatValueWithUnit(
                  imageValueAtCursor,
                  primaryImage.seriesMetadata.modality,
                  primaryImage
              )
            : null;

    const formattedSecondaryImageValue =
        secondaryImageValueAtCursor !== null &&
        secondaryImage?.seriesMetadata.modality
            ? formatValueWithUnit(
                  secondaryImageValueAtCursor,
                  secondaryImage.seriesMetadata.modality,
                  secondaryImage
              )
            : null;

    const formattedDoseValue =
        doseVisibility && doseIntensityValueAtCursor !== null
            ? formatDoseDisplayValue(doseIntensityValueAtCursor)
            : null;

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={0.25}
            onClick={handleExpand}
            data-testid="coordinate-display"
            sx={{
                cursor: 'pointer',
            }}
        >
            {formattedDoseValue && isActive && expanded && (
                <OverlayRow>
                    <Box
                        display="grid"
                        gridTemplateColumns={'auto 1fr'}
                        gap={0.5}
                    >
                        <OverlayLabel color="primary.light">D: </OverlayLabel>
                        <OverlayLabel
                            sx={{
                                width: 54,
                                justifyContent: 'flex-end',
                            }}
                        >
                            {formattedDoseValue}
                        </OverlayLabel>
                    </Box>
                </OverlayRow>
            )}

            {expanded &&
                isActive &&
                (formattedImageValue || formattedSecondaryImageValue) && (
                    <OverlayRow>
                        {formattedSecondaryImageValue ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                <OverlayLabel color="primary.light">
                                    I (P / S) :{' '}
                                </OverlayLabel>
                                <OverlayLabel>
                                    {formattedImageValue ?? ''}
                                </OverlayLabel>
                                <OverlayLabel>/</OverlayLabel>
                                <OverlayLabel>
                                    {formattedSecondaryImageValue}
                                </OverlayLabel>
                            </Box>
                        ) : (
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                <OverlayLabel color="primary.light">
                                    I:{' '}
                                </OverlayLabel>
                                <OverlayLabel>
                                    {formattedImageValue}
                                </OverlayLabel>
                            </Box>
                        )}
                    </OverlayRow>
                )}

            <OverlayRow>
                {expanded && isActive ? (
                    patientSpaceCoords?.coords && (
                        <React.Fragment>
                            {['X', 'Y', 'Z'].map((coord, index) => (
                                <Box
                                    key={coord}
                                    display="grid"
                                    gridTemplateColumns={'auto 1fr auto'}
                                    gap={0.5}
                                >
                                    <OverlayLabel
                                        sx={{ color: 'primary.main' }}
                                    >
                                        {coord}:
                                    </OverlayLabel>
                                    <OverlayLabel>
                                        {coord === axis
                                            ? primaryPosition
                                            : patientSpaceCoords?.coords?.[
                                                    index
                                                ] !== undefined
                                              ? (
                                                    patientSpaceCoords
                                                        ?.coords?.[index] / 10
                                                ).toFixed(2)
                                              : NaN}{' '}
                                    </OverlayLabel>
                                    <OverlayLabel> cm</OverlayLabel>
                                </Box>
                            ))}
                        </React.Fragment>
                    )
                ) : (
                    <Box
                        display="grid"
                        gridTemplateColumns={'auto 1fr auto'}
                        gap={0.5}
                    >
                        <OverlayLabel sx={{ color: 'primary.main' }}>
                            {axis} :
                        </OverlayLabel>
                        <OverlayLabel sx={{ justifyContent: 'flex-end' }}>
                            {primaryPosition}{' '}
                        </OverlayLabel>
                        <OverlayLabel> cm</OverlayLabel>
                    </Box>
                )}
            </OverlayRow>
        </Box>
    );
};

export default CoordinateDisplay;
